import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
  VStack,
  Link as ChakraLink,
} from '@chakra-ui/react';
import PageTitle from '../components/PageTitle';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useUserContext } from '../contexts/UserContext';
import { usePopulatedLeague } from '../hooks/usePopulatedLeague';
import { useScoresByDate } from '../hooks/useGameScores';
import TeamLogo from '../components/TeamLogo';
import { getTeamInfoById } from '../utils/getTeamInfo';
import BoxScoreModal from '../components/BoxScoreModal';
import PlayByPlayModal from '../components/PlayByPlayModal';
import BroadcastGameModal, { type BroadcastGameRow } from '../components/BroadcastGameModal';
import GlassCard from '../components/GlassCard';

type CtaKind = 'box' | 'recap' | 'detail' | 'broadcast' | 'live';

const LeagueScoreboardPage: React.FC = () => {
  const { teamId } = useParams<{ teamId?: string }>();
  const [searchParams] = useSearchParams();
  const queryMyGames = searchParams.get('mygames');
  const queryGameId = searchParams.get('gameId');
  const navigate = useNavigate();

  const myLeague = useMyLeagueStore((s) => s.myLeague);
  const { myTeam } = useUserContext();
  const myTeamId = myTeam ? Number(myTeam) : null;
  const leagueId = myLeague?.id;

  const [selectedDate, setSelectedDate] = useState(() => {
    return dayjs().startOf('day');
  });
  const [myGamesOnly, setMyGamesOnly] = useState(queryMyGames === 'true');
  const dateInputRef = useRef<HTMLInputElement>(null);

  const [isBoxOpen, setIsBoxOpen] = useState(false);
  const [isRecapOpen, setIsRecapOpen] = useState(false);
  const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);
  const toast = useToast();
  const [activeGameId, setActiveGameId] = useState<number | null>(() => {
    if (!queryGameId) return null;
    const n = Number(queryGameId);
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  const [activeGameRow, setActiveGameRow] = useState<BroadcastGameRow | null>(null);
  const broadcastAutoOpenedRef = useRef<number | null>(null);
  const broadcastNotFoundToastShownRef = useRef(false);

  const { data: league, isLoading: isLeagueLoading } = usePopulatedLeague(leagueId);
  const dateString = selectedDate.format('YYYY-MM-DD');
  const today = dayjs().startOf('day');
  const todayString = today.format('YYYY-MM-DD');
  const isSelectedDateToday = dateString === todayString;

  const { data: scoresByDate, isLoading } = useScoresByDate(leagueId, dateString, !!leagueId);
  const embargoed = Number(league?.embargoed ?? myLeague?.embargoed ?? 0);

  const teamAbbrToIdMap = useMemo(() => {
    if (!league) return new Map<string, number>();
    const map = new Map<string, number>();
    league.conferences?.forEach((conf: any) => {
      conf.divisions?.forEach((div: any) => {
        div.teams?.forEach((team: any) => {
          const abbr = team.rw_abbreviation || team.abbreviation;
          if (abbr) map.set(abbr, team.id);
          if (team.abbreviation && team.abbreviation !== team.rw_abbreviation) {
            map.set(team.abbreviation, team.id);
          }
        });
      });
    });
    return map;
  }, [league]);

  const teamNameToInfoMap = useMemo(() => {
    const map = new Map<string, any>();
    if (!league) return map;
    league.conferences?.forEach((conf: any) => {
      conf.divisions?.forEach((div: any) => {
        div.teams?.forEach((team: any) => {
          if (team?.name) map.set(String(team.name).toLowerCase(), team);
        });
      });
    });
    return map;
  }, [league]);

  const resolveTeamInfo = (row: Record<string, unknown>, side: 'away' | 'home') => {
    const idKey = `${side}_team_id`;
    const abbrKey = `${side}_abbr`;
    const nameKey = `${side}_team`;
    const teamId = Number(row[idKey] ?? teamAbbrToIdMap.get(String(row[abbrKey] ?? '')) ?? 0);
    let info: any = teamId ? getTeamInfoById(teamId, league as any) : null;
    if (!info) {
      const byName = String(row[nameKey] ?? '').toLowerCase().trim();
      if (byName) info = teamNameToInfoMap.get(byName) ?? null;
    }
    return info;
  };

  const filteredGames = useMemo(() => {
    const list = scoresByDate?.rows ?? [];
    const teamIdFilter = teamId ? Number(teamId) : null;
    return list.filter((g: Record<string, unknown>) => {
      const awayInfo = resolveTeamInfo(g, 'away');
      const homeInfo = resolveTeamInfo(g, 'home');
      const awayTeamId = Number(g.away_team_id ?? awayInfo?.id ?? teamAbbrToIdMap.get(String(g.away_abbr ?? '')) ?? 0);
      const homeTeamId = Number(g.home_team_id ?? homeInfo?.id ?? teamAbbrToIdMap.get(String(g.home_abbr ?? '')) ?? 0);
      if (teamIdFilter && awayTeamId !== teamIdFilter && homeTeamId !== teamIdFilter) return false;
      if (myGamesOnly && myTeam && awayTeamId !== myTeam && homeTeamId !== myTeam) return false;
      return true;
    });
  }, [scoresByDate?.rows, teamId, myGamesOnly, myTeam, teamAbbrToIdMap, teamNameToInfoMap]);

  const noGamesCopy = selectedDate.isAfter(dayjs(), 'day')
    ? `No games scheduled on ${selectedDate.format('MMM D, YYYY')}`
    : `No games played on ${selectedDate.format('MMM D, YYYY')}`;

  const toGameRow = (g: Record<string, unknown>): BroadcastGameRow => ({
    game_id: Number(g.game_id ?? 0),
    scheduled_date: String(g.scheduled_date ?? dateString),
    away_team_id: Number(g.away_team_id ?? resolveTeamInfo(g, 'away')?.id ?? teamAbbrToIdMap.get(String(g.away_abbr ?? '')) ?? 0),
    home_team_id: Number(g.home_team_id ?? resolveTeamInfo(g, 'home')?.id ?? teamAbbrToIdMap.get(String(g.home_abbr ?? '')) ?? 0),
    away_runs: Number(g.away_runs ?? 0),
    home_runs: Number(g.home_runs ?? 0),
    innings: Number(g.innings ?? 9),
  });

  const openModal = (kind: CtaKind, g: Record<string, unknown>) => {
    const row = toGameRow(g);
    setActiveGameId(row.game_id);
    setActiveGameRow(row);
    if (kind === 'box') setIsBoxOpen(true);
    if (kind === 'recap') setIsRecapOpen(true);
    if (kind === 'broadcast' || kind === 'live') {
      broadcastNotFoundToastShownRef.current = false;
      setIsBroadcastOpen(true);
    }
  };

  const getStatusAndCtas = (g: Record<string, unknown>) => {
    const isToday = isSelectedDateToday;
    const hasScores = g.away_runs != null && g.home_runs != null;
    if (embargoed === 0) {
      return {
        status: hasScores ? 'Final' : 'Scheduled',
        showScores: hasScores,
        ctas: ['box', 'recap', 'detail', 'broadcast'] as CtaKind[],
      };
    }
    if (embargoed === 1) {
      if (isToday) return { status: 'Live', showScores: false, ctas: ['live'] as CtaKind[] };
      return {
        status: hasScores ? 'Final' : 'Scheduled',
        showScores: hasScores,
        ctas: hasScores ? ['box', 'recap', 'detail', 'broadcast'] as CtaKind[] : [] as CtaKind[],
      };
    }
    // embargoed === 2
    if (isToday) return { status: 'Today at 2pm', showScores: false, ctas: [] as CtaKind[] };
    return {
      status: hasScores ? 'Final' : 'Scheduled',
      showScores: hasScores,
      ctas: hasScores ? ['box', 'recap', 'detail', 'broadcast'] as CtaKind[] : [] as CtaKind[],
    };
  };

  const renderCta = (cta: CtaKind, g: Record<string, unknown>) => {
    if (cta === 'detail') {
      return (
        <ChakraLink
          key={cta}
          as="button"
          color="blue.300"
          fontSize="xs"
          _hover={{ textDecoration: 'underline', color: 'blue.200' }}
          onClick={() => {
            const gid = Number(g.game_id ?? 0);
            if (!Number.isFinite(gid) || gid <= 0) return;
            navigate(`/game-score-details/${gid}`);
          }}
        >
          Detailed Scoring
        </ChakraLink>
      );
    }
    const label =
      cta === 'box'
        ? 'Box Score'
        : cta === 'recap'
        ? 'Play-By-Play'
        : cta === 'broadcast'
        ? 'Watch Replay'
        : 'Watch Live';
    return (
      <ChakraLink
        key={cta}
        as="button"
        color="blue.300"
        fontSize="xs"
        _hover={{ textDecoration: 'underline', color: 'blue.200' }}
        onClick={() => openModal(cta, g)}
      >
        {label}
      </ChakraLink>
    );
  };

  const closeModals = () => {
    setIsBoxOpen(false);
    setIsRecapOpen(false);
    setIsBroadcastOpen(false);
    setActiveGameId(null);
    setActiveGameRow(null);
  };

  const handleBroadcastGameNotFound = () => {
    if (broadcastNotFoundToastShownRef.current) return;
    broadcastNotFoundToastShownRef.current = true;
    closeModals();
    toast({
      title: 'Sorry, we cannot find the requested game broadcast',
      status: 'error',
      duration: 4500,
      isClosable: true,
      position: 'top',
    });
  };

  // Deep-link support: if the URL includes `?gameId=<id>`, auto-open broadcast.
  useEffect(() => {
    if (!queryGameId) return;
    if (!leagueId || isLeagueLoading) return;

    const n = Number(queryGameId);
    if (!Number.isFinite(n) || n <= 0) return;
    if (broadcastAutoOpenedRef.current === n) return;

    broadcastAutoOpenedRef.current = n;
    broadcastNotFoundToastShownRef.current = false;
    setActiveGameId(n);
    setActiveGameRow(null);
    setIsBroadcastOpen(true);
  }, [queryGameId, leagueId, isLeagueLoading]);

  return (
    <Container maxW="7xl" py={8}>
      <PageTitle mb={4}>Explorer League Scoreboard</PageTitle>

      <Flex align="center" justify="center" mt={6} mb={10} w="100%" position="relative" minH="36px">
        <Box position="absolute" left={0} top="50%" transform="translateY(-50%)">
          <Checkbox
            isChecked={myGamesOnly}
            onChange={(e) => setMyGamesOnly(e.target.checked)}
            isDisabled={!!teamId}
          >
            My games only
          </Checkbox>
        </Box>

        <HStack spacing={3} justify="center">
          <IconButton
            aria-label="Previous day"
            icon={<FiChevronLeft />}
            size="sm"
            onClick={() => setSelectedDate((d) => d.subtract(1, 'day').startOf('day'))}
          />
          <HStack spacing={2}>
            <Text fontSize="md" fontWeight="semibold">
              {selectedDate.format('ddd, MMM D, YYYY')}
            </Text>
            <Box position="relative" display="inline-flex" alignItems="center">
              <input
                ref={dateInputRef}
                type="date"
                value={selectedDate.format('YYYY-MM-DD')}
                max={todayString}
                onChange={(e) => {
                  const picked = dayjs(e.target.value).startOf('day');
                  if (picked.isAfter(today, 'day')) return;
                  setSelectedDate(picked);
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                }}
                aria-label="Pick scoreboard date"
              />
              <IconButton
                aria-label="Pick scoreboard date"
                icon={<FiCalendar />}
                size="sm"
                variant="ghost"
                pointerEvents="none"
              />
            </Box>
          </HStack>
          <IconButton
            aria-label="Next day"
            icon={<FiChevronRight />}
            size="sm"
            isDisabled={selectedDate.isSame(today, 'day')}
            onClick={() => setSelectedDate((d) => (d.isSame(today, 'day') ? d : d.add(1, 'day').startOf('day')))}
          />
          {!selectedDate.isSame(today, 'day') && (
            <Button size="sm" colorScheme="green" onClick={() => setSelectedDate(dayjs(todayString).startOf('day'))}>
              Today
            </Button>
          )}
        </HStack>
      </Flex>

      {isLoading || (Boolean(leagueId) && isLeagueLoading) ? (
        <Flex justify="center" align="center" minH="260px">
          <Spinner size="lg" />
        </Flex>
      ) : filteredGames.length === 0 ? (
        <Flex justify="center" align="center" minH="260px">
          <Text fontSize="xl" color="gray.400">
            {noGamesCopy}
          </Text>
        </Flex>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
          {filteredGames.map((g, idx) => {
            const status = getStatusAndCtas(g);
            const awayTeam = resolveTeamInfo(g, 'away');
            const homeTeam = resolveTeamInfo(g, 'home');
            const awayName = String(g.away_team_name ?? g.away_team ?? awayTeam?.name ?? 'Away');
            const homeName = String(g.home_team_name ?? g.home_team ?? homeTeam?.name ?? 'Home');
            const awayAbbr = String(g.away_abbr ?? awayTeam?.rw_abbreviation ?? awayTeam?.abbreviation ?? '').toUpperCase();
            const homeAbbr = String(g.home_abbr ?? homeTeam?.rw_abbreviation ?? homeTeam?.abbreviation ?? '').toUpperCase();
            const awayTeamId = Number(g.away_team_id ?? awayTeam?.id ?? teamAbbrToIdMap.get(String(g.away_abbr ?? '')) ?? 0);
            const homeTeamId = Number(g.home_team_id ?? homeTeam?.id ?? teamAbbrToIdMap.get(String(g.home_abbr ?? '')) ?? 0);
            const isMyGame = Boolean(myTeamId && (awayTeamId === myTeamId || homeTeamId === myTeamId));
            const cardKey = `${String(g.game_id ?? idx)}`;
            const cardBody = (
              <>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="xs" color="gray.400">{status.status}</Text>
                  {status.showScores && status.status !== 'Live' && g.innings && Number(g.innings) > 9 ? (
                    <Text fontSize="xs" color="gray.500">({Number(g.innings)})</Text>
                  ) : null}
                </HStack>
                <VStack align="stretch" spacing={2}>
                  <HStack>
                    <TeamLogo team={{ name: awayName, abbreviation: awayAbbr, logo: (awayTeam as any)?.logo, color_primary: (awayTeam as any)?.color_primary, color_secondary: (awayTeam as any)?.color_secondary, color_text: (awayTeam as any)?.color_text }} size="sm" />
                    <Text fontSize="sm" color="gray.100" isTruncated>{awayName}</Text>
                    {status.showScores ? <Text fontSize="sm" color="white" ml="auto">{Number(g.away_runs ?? 0)}</Text> : null}
                  </HStack>
                  <HStack>
                    <TeamLogo team={{ name: homeName, abbreviation: homeAbbr, logo: (homeTeam as any)?.logo, color_primary: (homeTeam as any)?.color_primary, color_secondary: (homeTeam as any)?.color_secondary, color_text: (homeTeam as any)?.color_text }} size="sm" />
                    <Text fontSize="sm" color="gray.100" isTruncated>{homeName}</Text>
                    {status.showScores ? <Text fontSize="sm" color="white" ml="auto">{Number(g.home_runs ?? 0)}</Text> : null}
                  </HStack>
                </VStack>
                <HStack mt={2} spacing={3} flexWrap="wrap">
                  {status.ctas.map((cta) => renderCta(cta, g))}
                </HStack>
              </>
            );
            return isMyGame ? (
              <GlassCard key={cardKey} borderRadius="sm" p={2}>
                {cardBody}
              </GlassCard>
            ) : (
              <Box key={cardKey} borderWidth="1px" borderColor="gray.700" borderRadius="sm" bg="gray.900" p={2}>
                {cardBody}
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {isBoxOpen && activeGameRow && (
        <BoxScoreModal isOpen={isBoxOpen} onClose={closeModals} gameId={activeGameId} gameRow={activeGameRow} league={league as any} />
      )}
      {isRecapOpen && activeGameRow && (
        <PlayByPlayModal isOpen={isRecapOpen} onClose={closeModals} gameId={activeGameId} gameRow={activeGameRow} league={league as any} />
      )}
      {isBroadcastOpen && activeGameId != null && (
        <BroadcastGameModal
          isOpen={isBroadcastOpen}
          onClose={closeModals}
          gameId={activeGameId}
          gameRow={activeGameRow}
          onGameNotFound={handleBroadcastGameNotFound}
          league={league as any}
        />
      )}
    </Container>
  );
};

export default LeagueScoreboardPage;
